import { isDefined } from "lib/utils";

type Filters = Record<string, unknown>;

type RangeFilter = { from?: string; to?: string; hideNulls?: boolean };

function isRangeFilterApplied(value: RangeFilter) {
  return value.hideNulls || isDefined(value.from) || isDefined(value.to);
}

export function getAppliedFilters<T extends Filters>(
  filters: T,
  omittedFilters?: string[],
): (keyof T)[] {
  const appliedFiltersArray = Object.entries(filters).filter(([key, value]) => {
    if (omittedFilters?.includes(key)) {
      return false;
    }

    if (!isDefined(value)) {
      return false;
    }

    if (typeof value === "boolean") {
      return value;
    }

    if (typeof value === "string") {
      return value.length > 0;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (value && typeof value === "object") {
      if ("from" in value || "to" in value || "hideNulls" in value) {
        return isRangeFilterApplied(value as RangeFilter);
      }

      const nestedFields = getAppliedFilters(value as Filters);

      return nestedFields.length ? true : false;
    }

    return false;
  });

  return appliedFiltersArray.map(([key]) => key as keyof T);
}

/**
 * Merges the `customBuyers` filter key into the shared `buyers` chip.
 * If `customBuyers` is active but `buyers` is not, promotes it to `buyers`.
 * Removes `customBuyers` from the list in all cases so only one chip is shown.
 */
export function mergeCustomBuyersChip<T extends string>(
  appliedFilters: (T | "buyers" | "customBuyers")[],
): (T | "buyers")[] {
  const withoutCustomBuyers = appliedFilters.filter((k): k is T | "buyers" => k !== "customBuyers");
  const hasCustomBuyers = appliedFilters.includes("customBuyers");
  const hasBuyers = appliedFilters.includes("buyers");
  if (hasCustomBuyers && !hasBuyers) {
    return [...withoutCustomBuyers, "buyers"];
  }
  return withoutCustomBuyers;
}

/**
 * Returns the filter update to apply when clearing the "Buyers" chip.
 * Since `buyers` and `customBuyers` share one chip, both are cleared together.
 */
export function clearBuyerFilters<T extends { buyers?: unknown; customBuyers?: unknown }>(
  key: keyof T,
): Partial<T> {
  if (key === "buyers" || key === "customBuyers") {
    return { buyers: undefined, customBuyers: undefined } as Partial<T>;
  }
  return { [key]: undefined } as Partial<T>;
}
